// src/components/game/veridian/VeridianEngine.jsx
import React, { useState, useEffect } from 'react';
import * as Data from './GrammarData';
import { ASSETS } from '../../../utils/GameAssets';
import ViralRecruiter from '../ui/ViralRecruiter';

// --- 1. CONFIGURATION ---
const VISUALS = {
    Infrastructure: "/assets/veridian/visuals/reactor_core_offline.jpg",
    Media: "/assets/veridian/visuals/mediator_plaza.jpg",
    Leadership: "/assets/veridian/visuals/leader_throne.jpg",
    SupplyChain: "/assets/veridian/visuals/supply_depot.jpg",
    Transport: "/assets/veridian/visuals/transport_hub.jpg",
    Surveillance: "/assets/veridian/visuals/security_room.jpg",
    Research: "/assets/veridian/visuals/science_lab.jpg",
    Authentication: "/assets/veridian/visuals/data_stream_overlay.mp4",
};

const SECTORS = {
    Infrastructure: { title: "Reactor Core", desc: "Stabilize energy flow.", protocol: "SUBJECT_VERB_AGREEMENT", generator: Data.generateSTrap, locked: false },
    Media: { title: "Mediator Plaza", desc: "Broadcast truth.", protocol: "TENSE_ALIGNMENT", generator: Data.generateTense, locked: false },
    Leadership: { title: "Throne Room", desc: "Issue commands.", protocol: "CAUSATIVE_STRUCTURES", generator: Data.generateCausative, locked: false },
    SupplyChain: { title: "Supply Depot", desc: "Manage inventory.", protocol: "QUANTIFIERS", pool: Data.SOME_ANY_POOL, locked: true, tier: 1 },
    Transport: { title: "Transport Hub", desc: "Optimize routes.", protocol: "PHRASAL_VERBS", pool: Data.PHRASAL_POOL, locked: true, tier: 1 },
    Surveillance: { title: "Security Room", desc: "Monitor threats.", protocol: "REPORTED_SPEECH", pool: Data.REPORTER_POOL, locked: true, tier: 3 },
    Authentication: { title: "Data Stream", desc: "Verify identity.", protocol: "TO_BE_PROTOCOLS", generator: Data.generateIdentity, locked: true, tier: 3 },
    Research: { title: "Science Lab", desc: "Predict outcomes.", protocol: "CONDITIONALS", pool: Data.FUTURE_POOL, locked: true, tier: 3 }
};

// --- 2. SOUND HELPER ---
const playSound = (key) => {
    if (!ASSETS || !ASSETS.AUDIO || !ASSETS.AUDIO[key]) return;
    try {
        const audio = new Audio(ASSETS.AUDIO[key]);
        audio.volume = key === 'Ambience' ? 0.2 : 0.4;
        const playPromise = audio.play();
        if (playPromise !== undefined) playPromise.catch(() => {});
    } catch (e) { console.warn("Audio error:", e); }
};

// --- 3. TYPEWRITER ---
const Typewriter = ({ text }) => {
    const [disp, setDisp] = useState("");
    useEffect(() => {
        setDisp(""); let i = 0;
        const timer = setInterval(() => {
            setDisp(text.slice(0, i+1));
            if (i % 2 === 0) playSound('Type');
            i++; if (i >= text.length) clearInterval(timer);
        }, 40);
        return () => clearInterval(timer);
    }, [text]);
    return <div className="font-mono text-cyan-400 text-lg md:text-xl leading-relaxed">{disp}<span className="animate-pulse text-white">_</span></div>;
};

// --- 4. MAIN ENGINE ---
export default function VeridianEngine() {
  const [currentSector, setCurrentSector] = useState('Infrastructure');
  const [civLevel, setCivLevel] = useState(0);
  const [shareCount, setShareCount] = useState(0); 
  const [showRecruiter, setShowRecruiter] = useState(false);
  const [question, setQuestion] = useState(null);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
      const saved = localStorage.getItem('veridian_shares');
      if (saved) setShareCount(parseInt(saved));
  }, []);

  useEffect(() => {
      let drone = null;
      if (ASSETS && ASSETS.AUDIO && ASSETS.AUDIO.Ambience) {
          drone = new Audio(ASSETS.AUDIO.Ambience);
          drone.loop = true;
          drone.volume = 0.15;
          drone.play().catch(() => {});
      }
      return () => { if (drone) drone.pause(); };
  }, []);

  const loadQuestion = (sectorKey) => {
      const sec = SECTORS[sectorKey];
      let qData = null;
      if (sec.generator) qData = sec.generator();
      else if (sec.pool) {
          const raw = sec.pool[Math.floor(Math.random() * sec.pool.length)];
          qData = { text: raw.text, options: raw.options.map(o => ({ id: o.id || o.label, label: o.label || o, correct: o.correct === true })).sort(() => Math.random() - 0.5) };
      }
      setQuestion(qData);
      setFeedback(null);
  };

  useEffect(() => { loadQuestion(currentSector); }, [currentSector]);

  const handleAnswer = (opt) => {
      if (opt.correct) {
          playSound('Success');
          setFeedback({ msg: "PROTOCOL VALIDATED.", type: "success" });
          setTimeout(() => { setCivLevel(p => Math.min(100, p + 5)); loadQuestion(currentSector); }, 1500);
      } else {
          playSound('Error');
          setFeedback({ msg: "SYNTAX ERROR.", type: "error" });
      }
  };

  const activeSector = SECTORS[currentSector];
  const bgSource = VISUALS[currentSector];
  const isVideo = bgSource && bgSource.endsWith('mp4');

  return (
    <div className="min-h-screen bg-slate-900 text-white font-mono relative overflow-hidden flex flex-col">
        <div className="absolute inset-0 z-0">
            {isVideo ? <video src={bgSource} autoPlay loop muted className="w-full h-full object-cover opacity-60" /> : <img src={bgSource} alt="Sector" className="w-full h-full object-cover opacity-60 transition-opacity duration-1000" />}
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-transparent to-black/80"></div>
            <div className="absolute inset-0 bg-[url('https://transparenttextures.com/patterns/dark-scan.png')] opacity-20 pointer-events-none"></div>
        </div>

        {showRecruiter && <ViralRecruiter onComplete={() => { playSound('Unlock'); const newCount = shareCount + 1; setShareCount(newCount); localStorage.setItem('veridian_shares', newCount); setShowRecruiter(false); }} onCancel={() => setShowRecruiter(false)} />}

        {/* TOP HUD */}
        <div className="relative z-10 p-4 border-b border-cyan-800 bg-black/70 flex justify-between items-center backdrop-blur-sm shadow-xl">
            <div>
                {/* SMALLER, NON-TYPEWRITER HEADER */}
                <h1 className="text-xl font-black font-sans text-cyan-500 tracking-widest uppercase border-b-2 border-cyan-500 pb-1">
                    PROJECT VERIDIAN <span className="text-xs text-gray-500 ml-2">v2.7</span>
                </h1>
            </div>
            
            <div className="flex gap-4 overflow-x-auto custom-scrollbar px-4">
                {Object.entries(SECTORS).map(([key, data]) => {
                    const isLocked = data.locked && shareCount < data.tier;
                    const isActive = currentSector === key;
                    return (
                        <button key={key} onClick={() => isLocked ? setShowRecruiter(true) : setCurrentSector(key)} className={`text-xs font-bold uppercase tracking-wider py-2 px-3 border-b-2 transition-all whitespace-nowrap ${isActive ? 'border-cyan-400 text-cyan-400 bg-cyan-900/40' : 'border-transparent text-gray-400 hover:text-gray-200'} ${isLocked ? 'opacity-50' : ''}`}>
                            {isLocked ? `🔒 ${data.title}` : data.title}
                        </button>
                    );
                })}
            </div>
            <div className="text-right">
                <div className="text-[10px] text-gray-400 tracking-widest">SYNC</div>
                <div className="text-xl font-bold text-green-400">{civLevel}%</div>
            </div>
        </div>

        {/* MAIN TERMINAL */}
        <div className="relative z-10 flex-grow container mx-auto p-4 lg:p-8 flex flex-col lg:flex-row gap-8 items-center justify-center">
            
            {/* LEFT PANEL */}
            <div className="flex-1 w-full max-w-xl bg-black/60 border border-slate-500/50 p-8 rounded-xl shadow-2xl backdrop-blur-md">
                <div className="mb-6 border-b border-gray-600 pb-4">
                    {/* HUGE ROOM NAME */}
                    <h2 className="text-7xl md:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-cyan-400 mb-6 drop-shadow-[0_0_15px_rgba(34,211,238,0.6)]">
                        {activeSector.title}
                    </h2>
                    <p className="text-sm text-cyan-400 font-bold tracking-[0.2em] uppercase mb-2">PROTOCOL: {activeSector.protocol}</p>
                    <p className="text-xl text-gray-300 italic font-serif">"{activeSector.desc}"</p>
                </div>
                
                <div className="bg-slate-900/80 p-4 rounded border-l-4 border-green-500 shadow-inner font-mono text-xs text-green-400 space-y-1">
                    <div>{">"} CONNECTING TO {currentSector.toUpperCase()} NODE...</div>
                    <div>{">"} ANALYZING LINGUISTIC PATTERNS...</div>
                    <div className="animate-pulse">{">"} AWAITING INPUT_</div>
                </div>
            </div>

            {/* RIGHT PANEL */}
            <div className="flex-1 w-full max-w-xl bg-slate-900/70 border-2 border-cyan-800/60 p-8 rounded-xl shadow-[0_0_60px_rgba(0,0,0,0.3)] backdrop-blur-md">
                <div className="mb-8 p-6 bg-black/50 border-l-4 border-cyan-500 rounded relative min-h-[100px] flex items-center">
                    <div className="absolute top-2 right-2 text-[9px] text-cyan-600 font-bold uppercase border border-cyan-900 px-2 py-0.5 rounded">Incoming Transmission</div>
                    {question ? <Typewriter text={question.text} /> : <span className="text-gray-500 animate-pulse">Initializing link...</span>}
                </div>

                <div className="grid grid-cols-1 gap-4">
                    {question && question.options.map((opt, i) => (
                        <button key={i} onClick={() => handleAnswer(opt)} className="w-full py-5 px-6 bg-slate-800/80 hover:bg-cyan-900/90 border border-slate-600 hover:border-cyan-400 text-white font-bold rounded-lg transition-all text-left group shadow-lg relative overflow-hidden">
                            <span className="text-cyan-500 mr-3 group-hover:text-white transition-colors">{">"}</span> 
                            <span className="text-lg tracking-wide">{opt.label}</span>
                            <div className="absolute inset-0 bg-white/5 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
                        </button>
                    ))}
                </div>

                {feedback && <div className={`mt-6 text-center font-bold p-3 rounded animate-fade-in border ${feedback.type === 'success' ? 'bg-green-900/60 border-green-500 text-green-400' : 'bg-red-900/60 border-red-500 text-red-400'}`}>{feedback.msg}</div>}
            </div>
        </div>
    </div>
  );
}